import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { initialsFromName } from "@/lib/utils";
import type { PortalDealHubspotOwner } from "@colab/shared";

// Read-only display of the HubSpot deal owner — a Bookboost internal who
// collaborates with the partner on the deal. Quality-bar invariant: the
// owner is VIEW-time only; it never becomes "actor" semantics for partner-
// side mutations.
//
// When the HubSpot owner email maps to an internal Bookboost profile we
// render the matched avatar; otherwise we fall back to initials from
// firstName+lastName. Render-nothing if no owner is set.

export function HubspotOwnerChip({
  owner,
}: {
  owner: PortalDealHubspotOwner | null;
}) {
  if (!owner) return null;
  const displayName =
    owner.matched?.fullName ||
    [owner.firstName, owner.lastName].filter(Boolean).join(" ") ||
    owner.email ||
    "Bookboost owner";
  const initials = initialsFromName(displayName);
  return (
    <div className="inline-flex items-center gap-2 rounded-full border bg-card px-2.5 py-1">
      <Avatar className="h-6 w-6">
        {owner.matched?.avatarUrl ? (
          <AvatarImage src={owner.matched.avatarUrl} alt={displayName} />
        ) : null}
        <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
      </Avatar>
      <div className="leading-tight">
        <p className="text-xs font-medium text-foreground">{displayName}</p>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
          HubSpot owner
        </p>
      </div>
    </div>
  );
}
